import type { AgGridReact, GridOptions } from "~/components/grid/AgGrid";
import type { InspectionRow } from "~/helpers/aiChat/migrationReview";

import { Box, Button, Chip, CircularProgress } from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import inspectionColumns from "~/components/fernie/columnDefs/inspectionColumnDefs";
import LINE_SX from "~/components/fernie/fernieStyles";
import AgGridWithoutRibbon from "~/components/grid/AgGridWithoutRibbon";
import { CaptionText } from "~/components/Typographies";
import migrationReview from "~/helpers/aiChat/migrationReview";
import useLocaleFormat from "~/hooks/useLocaleFormat";
import { useGetMigrationPreparedRowsQuery } from "~/services/endpoints/apiEndpoints";

interface FernieInspectionTableProps {
	fileName: string;
	journeyId: string;
	sourceId: string;
}

interface InspectionGridProps {
	fieldNames: string[];
	isError: boolean;
	onRetry: () => void;
	rows: InspectionRow[];
}

const InspectionGrid = ({ fieldNames, isError, onRetry, rows }: InspectionGridProps) => {
	const { t } = useTranslation("common");
	const gridRef = useRef<AgGridReact<InspectionRow>>(null!);
	const gridOptions: GridOptions<InspectionRow> = useMemo(
		() => ({
			columnDefs: inspectionColumns(fieldNames, t("fernieChat.review.table.sourceRows")),
			defaultColDef: { editable: false, filter: "agTextColumnFilter", floatingFilter: true, minWidth: 120 },
			suppressCellFocus: true
		}),
		[fieldNames, t]
	);

	return (
		<Box sx={{ height: 320 }}>
			<AgGridWithoutRibbon<InspectionRow>
				classSuffix="fit-height"
				embedded
				getRowId={(params) => params.data.id}
				gridDataKey="fernieMigrationInspection"
				gridOptions={gridOptions}
				gridRef={gridRef}
				isError={isError}
				onRetry={onRetry}
				rowData={isError ? [] : rows}
				suppressStatePersistence
			/>
		</Box>
	);
};

const FernieInspectionTable = ({ fileName, journeyId, sourceId }: FernieInspectionTableProps) => {
	const { t } = useTranslation("common");
	const { formatNumber } = useLocaleFormat();
	const [skip, setSkip] = useState(0);
	const { currentData, data, isError, refetch } = useGetMigrationPreparedRowsQuery({
		journeyId,
		skip,
		sourceId,
		take: migrationReview.inspectionPageSize
	});
	const shownPage = data?.migrationPreparedRows;
	const requestedPage = currentData?.migrationPreparedRows;
	const fieldNames = useMemo(() => migrationReview.inspectionFieldNames(shownPage?.rows ?? []), [shownPage]);
	const rows = useMemo(() => migrationReview.toInspectionRows(shownPage?.rows ?? []), [shownPage]);

	const renderRows = () => {
		if (!shownPage && !isError) return <CircularProgress size={16} />;
		if (shownPage?.expired) return <CaptionText sx={LINE_SX}>{t("fernieChat.review.table.expired")}</CaptionText>;
		if (rows.length === 0 && !isError)
			return <CaptionText sx={LINE_SX}>{t("fernieChat.review.table.empty")}</CaptionText>;
		return (
			<>
				<InspectionGrid
					fieldNames={fieldNames}
					isError={isError}
					key={`${isError}${fieldNames.join("\n")}`}
					onRetry={refetch}
					rows={rows}
				/>
				<Box sx={{ alignItems: "center", display: "flex", gap: 1, justifyContent: "space-between" }}>
					<Button
						disabled={skip === 0}
						onClick={() => setSkip(Math.max(skip - migrationReview.inspectionPageSize, 0))}
						size="small"
					>
						{t("fernieChat.review.table.previous")}
					</Button>
					{requestedPage && (
						<CaptionText sx={{ fontSize: "0.75rem" }}>
							{t("fernieChat.review.table.range", {
								from: formatNumber(skip + 1),
								to: formatNumber(skip + requestedPage.rows.length),
								total: formatNumber(requestedPage.total)
							})}
						</CaptionText>
					)}
					<Button
						disabled={!requestedPage || skip + requestedPage.rows.length >= requestedPage.total}
						onClick={() => setSkip(skip + migrationReview.inspectionPageSize)}
						size="small"
					>
						{t("fernieChat.review.table.next")}
					</Button>
				</Box>
			</>
		);
	};

	return (
		<Box
			aria-label={t("fernieChat.review.table.label", { fileName })}
			component="section"
			sx={{ display: "flex", flexDirection: "column", gap: 0.75, mt: 0.5 }}
		>
			<Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
				{shownPage && (
					<Chip label={t("fernieChat.review.table.revision", { revision: shownPage.revisionNumber })} size="small" />
				)}
				<CaptionText sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
					{t("fernieChat.review.table.inspectOnly")}
				</CaptionText>
			</Box>
			{renderRows()}
		</Box>
	);
};

export default FernieInspectionTable;
